import React, { useState, useEffect } from "react";
import { HiOutlineTrash } from "react-icons/hi";
import LearningLibraryModal from "./LearningLibraryModal";

// Chevron icon for accordion
const ChevronDown = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Individual Entry Row Component
const EntryRow = React.memo(function EntryRow({ goalEntry, onSave, isSaving, onOpenModal, onRemove }) {
  const [saveStatus, setSaveStatus] = useState(null);
  const [localEntry, setLocalEntry] = useState({
    developmentPlanText: goalEntry?.developmentPlanText || "",
    onTheJobApplication: goalEntry?.onTheJobApplication || "",
    mentoringPeerSupport: goalEntry?.mentoringPeerSupport || "",
    formalLearning: Array.isArray(goalEntry?.formalLearning) ? goalEntry.formalLearning : [],
  });

  // Check if there are unsaved changes
  const hasChanges =
    localEntry.developmentPlanText !== (goalEntry?.developmentPlanText || "") ||
    localEntry.onTheJobApplication !== (goalEntry?.onTheJobApplication || "") ||
    localEntry.mentoringPeerSupport !== (goalEntry?.mentoringPeerSupport || "");

  // Sync text fields when goalEntry prop changes
  useEffect(() => {
    setLocalEntry(prev => ({
      ...prev,
      developmentPlanText: goalEntry?.developmentPlanText || "",
      onTheJobApplication: goalEntry?.onTheJobApplication || "",
      mentoringPeerSupport: goalEntry?.mentoringPeerSupport || "",
    }));
  }, [goalEntry?.developmentPlanText, goalEntry?.onTheJobApplication, goalEntry?.mentoringPeerSupport]);

  // Sync formalLearning when goalEntry changes
  useEffect(() => {
    setLocalEntry(prev => ({
      ...prev,
      formalLearning: Array.isArray(goalEntry?.formalLearning) ? goalEntry.formalLearning : [],
    }));
  }, [goalEntry?.entryIndex, goalEntry?.formalLearning]);

  // Clear "saved" status after 2 seconds
  useEffect(() => {
    if (saveStatus === 'saved') {
      const timer = setTimeout(() => setSaveStatus(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [saveStatus]);

  const handleFieldChange = (field, value) => {
    setLocalEntry((prev) => ({ ...prev, [field]: value }));
    setSaveStatus(null);
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    setSaveStatus('saving');
    try {
      await onSave(goalEntry.entryIndex, {
        developmentPlanText: localEntry.developmentPlanText,
        onTheJobApplication: localEntry.onTheJobApplication,
        mentoringPeerSupport: localEntry.mentoringPeerSupport,
      });
      setSaveStatus('saved');
    } catch (err) {
      setSaveStatus(null);
      alert("Failed to save. Please try again.");
    }
  };

  const handleRemoveResource = async (resourceId, resourceTitle) => {
    if (resourceId) {
      try {
        await onSave(goalEntry.entryIndex, {
          formalLearning: { action: 'remove', resourceId }
        });
        setLocalEntry((prev) => ({
          ...prev,
          formalLearning: prev.formalLearning.filter(r =>
            resourceId ? r.resourceId !== resourceId : r.resourceTitle !== resourceTitle
          ),
        }));
      } catch {
        // API failed — don't update local state
      }
    } else {
      setLocalEntry((prev) => ({
        ...prev,
        formalLearning: prev.formalLearning.filter(r => r.resourceTitle !== resourceTitle),
      }));
    }
  };

  return (
    <>
      <tr className="data-row">
        {/* Development Plan - editable by user */}
        <td className="goal-col">
          <div className="goal-cell">
            <textarea
              className="entry-textarea dev-plan-textarea"
              placeholder="Write your development plan..."
              value={localEntry.developmentPlanText}
              onChange={(e) => handleFieldChange("developmentPlanText", e.target.value)}
              rows={4}
            />
          </div>
        </td>
        <td className="data-col">
          <textarea
            className="entry-textarea"
            placeholder="Describe your experience-based assignment..."
            value={localEntry.onTheJobApplication}
            onChange={(e) => handleFieldChange("onTheJobApplication", e.target.value)}
            rows={4}
          />
        </td>
        <td className="data-col">
          <textarea
            className="entry-textarea"
            placeholder="Describe your monitoring and coaching activities..."
            value={localEntry.mentoringPeerSupport}
            onChange={(e) => handleFieldChange("mentoringPeerSupport", e.target.value)}
            rows={4}
          />
        </td>
        <td className="data-col formal-learning-col">
          <div className="formal-learning-content">
            <div className="resource-list">
              {localEntry.formalLearning.map((resource, idx) => (
                <div key={resource.resourceId || `${resource.resourceTitle}-${idx}`} className="resource-item">
                  <span className="resource-type">{resource.resourceType}:</span>
                  <a
                    href={resource.resourceUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resource-link"
                    onClick={(e) => !resource.resourceUrl && e.preventDefault()}
                  >
                    {resource.resourceTitle}
                  </a>
                  <button
                    className="remove-resource-btn"
                    onClick={() => handleRemoveResource(resource.resourceId, resource.resourceTitle)}
                    type="button"
                    title="Remove resource"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              className="browse-btn"
              onClick={() => onOpenModal(goalEntry.entryIndex)}
              type="button"
            >
              Browse Library →
            </button>
          </div>
        </td>
        <td className="delete-col">
          <button
            className="remove-entry-btn"
            onClick={() => onRemove(goalEntry.entryIndex)}
            type="button"
            title="Remove this entry"
          >
            <HiOutlineTrash size={16} />
          </button>
        </td>
      </tr>

      {/* Row-level save indicator */}
      {hasChanges && (
        <tr className="save-indicator-row">
          <td colSpan="5">
            <div className="row-save-section">
              <span className="unsaved-text">Unsaved changes</span>
              <button
                className="save-btn-small"
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
                type="button"
              >
                {saveStatus === 'saving' ? 'Saving...' : 'Save'}
              </button>
              {saveStatus === 'saved' && <span className="save-status saved">✓</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
});

// Main CompetencyTable component
export default function CompetencyTable({
  competencyName,
  competencyIndex,
  goalEntries = [],
  recommendations = [],
  recommendedResources = [],
  onSave,
  onAddEntry,
  onRemoveEntry,
  isSaving,
}) {
  const [expanded, setExpanded] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeEntryIndex, setActiveEntryIndex] = useState(null);
  const [showRecs, setShowRecs] = useState(false);
  const [recTab, setRecTab] = useState("plans"); // "plans" or "resources"

  const handleOpenModal = (entryIndex) => {
    setActiveEntryIndex(entryIndex);
    setModalOpen(true);
  };

  const handleResourceSelect = (resource) => {
    if (activeEntryIndex !== null) {
      onSave(activeEntryIndex, {
        formalLearning: { action: 'add', resourceId: resource._id }
      });
    }
  };

  // Adopt a recommendation — create a new entry with the recommendation text pre-filled
  const handleAdoptRecommendation = async (rec) => {
    await onAddEntry(competencyIndex, {
      developmentPlanText: rec.goalDescription || rec.goalName || "",
    });
  };

  return (
    <div className="dev-accordion-item">
      <button
        className={`dev-accordion-trigger ${expanded ? "expanded" : ""}`}
        onClick={() => setExpanded(!expanded)}
        type="button"
      >
        <span className="dev-accordion-title">{competencyName}</span>
        <div className="dev-accordion-right">
          <span className="goal-count-badge">
            {goalEntries.length} entr{goalEntries.length !== 1 ? 'ies' : 'y'}
          </span>
          <ChevronDown className={`dev-accordion-chevron ${expanded ? "rotated" : ""}`} />
        </div>
      </button>

      {expanded && (
        <div className="dev-accordion-content">
          {goalEntries.length > 0 && (
            <div className="table-wrap">
              <table className="plan-table">
                <thead>
                  <tr className="lead-dev-first">
                    <th>Development Plan</th>
                    <th
                      className="tooltip-header"
                      data-tooltip="Put new skills into practice through real work. This is where your learning becomes behavior change."
                    >
                      Experience-Based Assignment <span className="info-icon">i</span>
                      <span className="column-percentage">70% of your development</span>
                    </th>
                    <th
                      className="tooltip-header"
                      data-tooltip="Support and feedback from your leaders or peers to help you grow with guidance and accountability."
                    >
                      Monitoring, Coaching & Professional Learning <span className="info-icon">i</span>
                      <span className="column-percentage">20% of your development</span>
                    </th>
                    <th
                      className="tooltip-header"
                      data-tooltip="Your tailored learning experiences to learn new or reinforce core concepts and skills."
                    >
                      Formal Training <span className="info-icon">i</span>
                      <span className="column-percentage">10% of your development</span>
                    </th>
                    <th className="delete-col-header"></th>
                  </tr>
                </thead>
                <tbody>
                  {goalEntries.map((goalEntry) => (
                    <EntryRow
                      key={`entry-${goalEntry.entryIndex}`}
                      goalEntry={goalEntry}
                      onSave={onSave}
                      isSaving={isSaving}
                      onOpenModal={handleOpenModal}
                      onRemove={onRemoveEntry}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {goalEntries.length === 0 && (
            <p className="empty-entries-hint">
              No entries yet. Click "Add Entry" below to get started.
            </p>
          )}

          {/* Actions row: Add Entry + Show Recommendations */}
          <div className="competency-actions-row">
            <button
              className="add-entry-btn"
              onClick={() => onAddEntry(competencyIndex)}
              type="button"
            >
              + Add Entry
            </button>
            {(recommendations.length > 0 || recommendedResources.length > 0) && (
              <button
                className="show-recs-btn"
                onClick={() => setShowRecs(!showRecs)}
                type="button"
              >
                {showRecs ? "Hide" : "Show"} Recommendations
              </button>
            )}
          </div>

          {/* Recommendation Drawer */}
          {showRecs && (
            <div className="recommendation-drawer-inline">
              {/* Tabs */}
              {recommendations.length > 0 && recommendedResources.length > 0 && (
                <div className="rec-tabs">
                  <button
                    className={`rec-tab ${recTab === "plans" ? "active" : ""}`}
                    onClick={() => setRecTab("plans")}
                    type="button"
                  >
                    Plans ({recommendations.length})
                  </button>
                  <button
                    className={`rec-tab ${recTab === "resources" ? "active" : ""}`}
                    onClick={() => setRecTab("resources")}
                    type="button"
                  >
                    Resources ({recommendedResources.length})
                  </button>
                </div>
              )}

              {/* Plans tab */}
              {(recTab === "plans" || recommendedResources.length === 0) && recommendations.length > 0 && (
                <div className="recs-list">
                  {recommendations.map((rec, idx) => (
                    <div key={rec._id || idx} className="rec-item">
                      <p className="rec-item-text">{rec.goalDescription || rec.goalName}</p>
                      <button
                        type="button"
                        className="rec-action-btn"
                        onClick={() => handleAdoptRecommendation(rec)}
                      >
                        + Add
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Resources tab */}
              {(recTab === "resources" || recommendations.length === 0) && recommendedResources.length > 0 && (
                <div className="recs-list">
                  {recommendedResources.map((resource, idx) => {
                    const isAdded = goalEntries.some(e =>
                      (e.formalLearning || []).some(fl =>
                        fl.resourceId && resource.resourceId &&
                        String(fl.resourceId) === String(resource.resourceId)
                      )
                    );
                    const firstEntry = goalEntries[0];

                    return (
                      <div key={resource.resourceId || idx} className="rec-item">
                        <div className="rec-item-info">
                          <span className="rec-item-type">{resource.resourceType || 'Resource'}</span>
                          <span className="rec-item-text">{resource.resourceTitle}</span>
                        </div>
                        {isAdded ? (
                          <span className="rec-added-label">Added</span>
                        ) : firstEntry ? (
                          <button
                            type="button"
                            className="rec-action-btn"
                            onClick={() => onSave(firstEntry.entryIndex, {
                              formalLearning: { action: 'add', resourceId: resource.resourceId }
                            })}
                          >
                            + Add
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <LearningLibraryModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setActiveEntryIndex(null);
        }}
        onSelect={handleResourceSelect}
      />
    </div>
  );
}
